import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { QuickDateRange, currentYearRange } from '../../components/ui/DateField'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { ExportDropdown } from '../../components/ui/ExportDropdown'
import { downloadXlsx } from '../../lib/downloadXlsx'

interface EmagSupplierInvoice {
  id: number
  number: string
  category: string
  categoryName: string
  date: string
  isStorno: boolean
  totalNoVat: string
  totalVat: string
  totalWithVat: string
  currency: string
}

interface CategoryAggregate {
  category: string
  categoryName: string
  count: number
  totalWithVat: string
}

// Grupare logică a categoriilor de comision eMAG.
const CATEGORY_GROUPS: { label: string; codes: string[] }[] = [
  { label: 'Comision', codes: ['FC', 'FCS'] },
  { label: 'Livrare (Genius)', codes: ['FED'] },
  { label: 'Vouchere & reduceri', codes: ['FV', 'FVS', 'FCDP'] },
  { label: 'Retururi', codes: ['FFR'] },
  { label: 'Marketing / Ads', codes: ['FAPO'] },
  { label: 'FBE (fulfillment)', codes: ['FFO', 'FHR', 'FFD', 'FFX', 'FFDP', 'FFS'] },
]

export function FacturiCumparareEmagPage() {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(50)
  const [category, setCategory] = useState<string>('')
  const [groupKey, setGroupKey] = useState<string>('') // label grup selectat
  const [catOpen, setCatOpen] = useState(true)
  const [dateFrom, setDateFrom] = useState(currentYearRange().from)
  const [dateTo, setDateTo] = useState(currentYearRange().to)

  const { data, isLoading } = useQuery<{
    invoices: EmagSupplierInvoice[]
    total: number
    byCategory: CategoryAggregate[]
  }>({
    queryKey: ['emag-supplier-invoices', page, pageSize, category, groupKey, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('limit', String(pageSize))
      params.set('offset', String(page * pageSize))
      const grp = CATEGORY_GROUPS.find((g) => g.label === groupKey)
      if (grp) params.set('categories', grp.codes.join(','))
      else if (category) params.set('category', category)
      if (dateFrom) params.set('dateFrom', dateFrom)
      if (dateTo) params.set('dateTo', dateTo)
      return (await api.get(`/emag/supplier-invoices?${params}`)).data
    },
  })

  const selectCategory = (code: string) => {
    setCategory(code)
    setGroupKey('')
    setPage(0)
    setCatOpen(false)
  }
  const selectGroup = (label: string) => {
    setGroupKey(label)
    setCategory('')
    setPage(0)
    setCatOpen(false)
  }
  const clearFilter = () => {
    setCategory('')
    setGroupKey('')
    setPage(0)
  }

  const [exporting, setExporting] = useState(false)
  const exportInvoices = async (currentPageOnly: boolean) => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (currentPageOnly) {
        params.set('limit', String(pageSize))
        params.set('offset', String(page * pageSize))
      }
      const grp = CATEGORY_GROUPS.find((g) => g.label === groupKey)
      if (grp) params.set('categories', grp.codes.join(','))
      else if (category) params.set('category', category)
      if (dateFrom) params.set('dateFrom', dateFrom)
      if (dateTo) params.set('dateTo', dateTo)
      await downloadXlsx(`/emag/supplier-invoices/export?${params.toString()}`)
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  const categoryStats = data?.byCategory ?? []

  const used = new Set<string>()
  const groups = CATEGORY_GROUPS.map((g) => {
    const items = g.codes
      .map((code) => categoryStats.find((c) => c.category === code))
      .filter((c): c is CategoryAggregate => !!c)
    items.forEach((c) => used.add(c.category))
    return { label: g.label, items }
  }).filter((g) => g.items.length > 0)
  const others = categoryStats.filter((c) => !used.has(c.category))
  if (others.length > 0) groups.push({ label: 'Altele', items: others })

  const ron = (n: number) => n.toLocaleString('ro-RO', { maximumFractionDigits: 0 })

  const activeLabel = groupKey
    ? groupKey
    : category
      ? (categoryStats.find((c) => c.category === category)?.categoryName ?? category)
      : 'Toate'

  const CategoryButton = ({ c }: { c: CategoryAggregate }) => (
    <button
      onClick={() => selectCategory(c.category)}
      className={`flex w-full flex-col items-start rounded border px-3 py-2 text-left transition ${
        category === c.category
          ? 'border-b4l-text bg-b4l-text text-white'
          : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
      }`}
    >
      <span className="text-xs font-medium leading-tight">
        {c.categoryName} <span className="font-mono opacity-70">({c.category})</span>
      </span>
      <span
        className={`mt-0.5 text-[11px] ${category === c.category ? 'text-white/80' : 'text-b4l-text-muted'}`}
      >
        {c.count} fact · {ron(Number(c.totalWithVat))} RON
      </span>
    </button>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          eMAG → Facturi cumpărare (de la eMAG către Boost4Life)
        </h1>
        <ExportDropdown
          loading={exporting}
          pageCount={data?.invoices?.length}
          totalCount={data?.total}
          onExportPage={() => exportInvoices(true)}
          onExportAll={() => exportInvoices(false)}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-medium">Categorii</span>
            {(category || groupKey) && (
              <span className="flex items-center gap-1 rounded bg-b4l-blue-light px-2 py-0.5 text-xs text-b4l-blue-dark">
                Filtru: {activeLabel}
                <button onClick={clearFilter} className="font-bold hover:text-b4l-red-dark">
                  ×
                </button>
              </span>
            )}
          </div>
          <button
            onClick={() => setCatOpen((o) => !o)}
            className="rounded border border-b4l-border bg-white px-3 py-1 text-xs font-medium hover:bg-b4l-bg-soft"
          >
            {catOpen ? 'Ascunde categorii ▲' : 'Afișează categorii ▼'}
          </button>
        </div>

        {catOpen && (
          <div className="mt-3">
            <button
              onClick={clearFilter}
              className={`mb-3 rounded-md border px-3.5 py-2 text-sm font-semibold ${
                !category && !groupKey
                  ? 'border-b4l-text bg-b4l-text text-white'
                  : 'border-b4l-border bg-white'
              }`}
            >
              Toate ({ron(categoryStats.reduce((a, c) => a + c.count, 0))} fact)
            </button>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {groups.map((g) => {
                const gCount = g.items.reduce((a, c) => a + c.count, 0)
                const gVal = g.items.reduce((a, c) => a + Number(c.totalWithVat), 0)
                const isGroupActive = groupKey === g.label
                return (
                  <div key={g.label} className="flex flex-col">
                    <button
                      onClick={() => selectGroup(g.label)}
                      title="Filtrează tot grupul"
                      className={`mb-1.5 rounded px-1.5 py-1 text-left text-xs font-semibold transition ${
                        isGroupActive
                          ? 'bg-b4l-text text-white'
                          : 'text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text'
                      }`}
                    >
                      {g.label}
                    </button>
                    <div className="flex-1 space-y-2">
                      {g.items.map((c) => (
                        <CategoryButton key={c.category} c={c} />
                      ))}
                    </div>
                    <div className="mt-2 border-t border-b4l-border pt-1.5 text-[11px] font-semibold text-b4l-text">
                      {gCount} fact · {ron(gVal)} RON
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </Card>

      <Card title="Filtre">
        <QuickDateRange
          fromValue={dateFrom}
          toValue={dateTo}
          onChange={(f, t) => {
            setDateFrom(f)
            setDateTo(t)
          }}
        />
      </Card>

      <Card>
        {isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {data?.invoices?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio factură găsită.</p>
        )}
        {data?.invoices && data.invoices.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                <tr>
                  <th className="py-2 pr-3">Număr</th>
                  <th className="py-2 pr-3">Categorie</th>
                  <th className="py-2 pr-3">Data</th>
                  <th className="py-2 pr-3 text-right">Fără TVA</th>
                  <th className="py-2 pr-3 text-right">TVA</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id} className="border-b border-b4l-border">
                    <td className="py-2 pr-3 font-mono">{i.number}</td>
                    <td className="py-2 pr-3">
                      {i.categoryName}{' '}
                      <span className="text-[10px] opacity-60">({i.category})</span>
                    </td>
                    <td className="py-2 pr-3">{new Date(i.date).toLocaleDateString('en-GB')}</td>
                    <td className="py-2 pr-3 text-right">{Number(i.totalNoVat).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right">{Number(i.totalVat).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right font-medium">
                      {Number(i.totalWithVat).toFixed(2)} {i.currency}
                    </td>
                    <td className="py-2 pr-3">
                      {i.isStorno ? (
                        <span className="rounded bg-b4l-red-light px-1 text-b4l-red-dark">
                          STORNO
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data?.total != null && (
          <div className="mt-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data.total}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s)
                setPage(0)
              }}
              itemLabel="facturi"
            />
          </div>
        )}
      </Card>
    </div>
  )
}
